// What the Account cell's hover card says: the full name, size and industry, then every flag
// spelled out. Pure, so the card on the board, Accounts, Available and AE tables reads one set of
// facts. Context only: nothing here feeds a sort, a score or a route.

import type { BankRequest, BookAccount } from '../api/types';
import { formatCount, httpsUrl } from '../lib/format.ts';
import { hasSharedSlack, pastOpportunityDetail, pastOpportunityOf } from './accountFlags.ts';
import { competingAskLine, competingCountOf, competingMarkLine, flagReasonsLine, isFlaggedRequest } from './bankRequest.ts';
import { autoLaneTitle, meetingBooked } from './boardPref.ts';
import { recentLostDeal } from './derived.ts';

export type RequestFlags = Pick<BankRequest, 'exclusion_reason' | 'exclusion_detail' | 'competing_count' | 'reasons'>;

export interface AccountCardFact {
  label: string;
  detail: string;
  // The Slack channel, when Salesforce holds a url.
  url: string | null;
}

// "5,800 employees" and the industry, each null when the row has none.
export function accountCardProfile(row: BookAccount): { employees: string | null; industry: string | null } {
  const count = row.employee_count ?? row.clay?.engine?.employees ?? null;
  return {
    employees: count == null ? null : `${formatCount(count)} employees`,
    industry: row.industry || row.clay?.engine?.sector || row.clay?.engine?.industry || null,
  };
}

// Available's two request marks, as facts: other asks on the account, and the gate's reasons.
export function requestFlagFacts(request: RequestFlags | null | undefined, competingTotal?: number): AccountCardFact[] {
  const facts: AccountCardFact[] = [];
  const competingText = competingTotal != null
    ? competingMarkLine(competingTotal)
    : competingAskLine(competingCountOf(request ?? null));
  if (competingText) facts.push({ label: 'Competing asks', detail: competingText, url: null });
  if (isFlaggedRequest(request)) {
    facts.push({ label: 'Flagged reasons', detail: flagReasonsLine(request), url: null });
  }
  return facts;
}

// Every flag the account carries, in the order the chips draw them.
export function accountCardFacts(row: BookAccount, today: Date, requestFacts: AccountCardFact[] = []): AccountCardFact[] {
  const facts: AccountCardFact[] = [];
  if (hasSharedSlack(row)) {
    facts.push({ label: 'Shared Slack', detail: 'Slack Connect channel with this company', url: httpsUrl(row.pylon_slack_url) });
  }
  const past = pastOpportunityOf(row);
  if (past) facts.push({ label: 'Past opportunity', detail: pastOpportunityDetail(past), url: null });
  const lost = recentLostDeal(row, today);
  if (lost) facts.push({ label: 'Lost deal', detail: `Closed lost ${lost.when}`, url: null });
  if (meetingBooked(row)) facts.push({ label: 'Meeting booked', detail: row.next_meeting ? `Next ${row.next_meeting}` : 'A meeting is on the calendar or held', url: null });
  const autoText = row.auto_lane ? autoLaneTitle(row) : null;
  if (autoText) facts.push({ label: 'Auto lane', detail: autoText, url: null });
  if (row.competitor) facts.push({ label: 'Competitor', detail: 'Salesforce marks this company a competitor', url: null });
  facts.push(...requestFacts);
  return facts;
}
